import type { AxiosInstance } from "axios";
import type { TranslateFn, U2000ApiErrorResult } from "../u2000Core/axios/U2000HandleApiError";
import type { U2000SearchPayload, U2000SearchParameter } from "../u2000Core/components/tables/basic/U2000BasicTable";
import { U2000GenerateUrlUUID } from "../u2000Core/tools/U2000UrlHelpers";

const LOGICALUNIT_URL = "U3141";

// Definition of the Logical Unit Endpoints
type U3141LandRegisterEndpointsType = {
  INITLU: string;
  SEARCH: string;

  VIEWDOCUMENT: (documentId: string | number) => string;
  /** Proxy binaire NAS/cache S3 (BL-0472) — index / plan / historique. */
  VIEWDOCUMENTPDFFILE: (documentId: string | number) => string;

  // Master Lot Chain Endpoints
  GET: (id: string | number) => string;
  CREATE: string;
  UPDATE: (id: string | number) => string;
  DELETE: (id: string | number) => string;
  AUDITS: (id: string | number) => string;

  // Child Lot Chain Endpoints
  GETCHILDLOTCHAIN: (id: string | number, childLotChainId: string | number) => string;
  CREATECHILDLOTCHAIN: (id: string | number) => string;
  UPDATECHILDLOTCHAIN: (id: string | number, childLotChainId: string | number) => string;
  DELETECHILDLOTCHAIN: (id: string | number, childLotChainId: string | number) => string;
  LOCKCHILDLOTCHAIN: (id: string | number, childLotChainId: string | number) => string;
  UNLOCKCHILDLOTCHAIN: (id: string | number, childLotChainId: string | number) => string;
  FETCHPROPERTYINDEX: (id: string | number, childLotChainId: string | number) => string;
  FETCHANNOTATEDLOTCHAININDEXPDF: (id: string | number, childLotChainId: string | number) => string;
  FETCHANNOTATEDLOTCHAININDEXPDFDRYRUN: (id: string | number, childLotChainId: string | number) => string;
  CHILDLOTCHAINAUDITS: (id: string | number, childLotChainId: string | number) => string;
  FINDPROPERTYDISTRICTS: string;
  FINDCADASTRES: string;
  FINDPROJECT: string;
  FINDREQUESTEDLOT: string;
  EXPORT_EXCEL: string;
  FETCHCADASTRALHISTORY: (id: string | number) => string;
  /** Import PDF de chaîne surligné → job U3141_RECONSTITUTE_CHAIN_FROM_PDF (BL-0562). */
  RECONSTITUTECHAINFROMPDF: (id: string | number) => string;
  CADASTRALHISTORYDOCUMENT: (id: string | number) => string;
  INDEXDOCUMENT: (masterLotChainId: string | number, childLotChainId: string | number) => string;
  PLANDOCUMENT: (masterLotChainId: string | number, childLotChainId: string | number) => string;
  SETVISIBLECHILDLOTCHAIN: (masterLotChainId: string | number, childLotChainId: string | number) => string;
  /** Mise à jour visible en bulk (recommandé). */
  SETVISIBLE: (masterLotChainId: string | number) => string;
  /** Positions des nœuds du graphe (childLotChainId → { x, y }). */
  NODEPOSITIONS: (masterLotChainId: string | number) => string;
  /** Données de la chaîne de lots pour le graphe (Voir la chaîne de lots). */
  LOTCHAINGRAPH: (masterLotChainId: string | number) => string;
  /** Liste des documents du child lot chain (onglet Documents). */
  GETDOCUMENTS: (masterLotChainId: string | number, childLotChainId: string | number) => string;
  /** Création d'un document (POST multipart). */
  POSTDOCUMENT: (masterLotChainId: string | number, childLotChainId: string | number) => string;
  /** Suppression d'un document. */
  DELETEDOCUMENT: (masterLotChainId: string | number, childLotChainId: string | number, documentId: string | number) => string;
  /** Mise à jour partielle d'un document (ex. renommage documentName). */
  PATCHDOCUMENT: (masterLotChainId: string | number, childLotChainId: string | number, documentId: string | number) => string;
  /** Mint jeton d'upload de plans ZIP (programme externe). */
  PLAN_UPLOAD_TOKEN: (masterLotChainId: string | number) => string;
  /** Proxy manifeste version.json siteAppliSFoncier (BL-0456). */
  SITEAPPLI_VERSION: string;
};

// Definition of the Logical Unit Endpoints
const u3141LandRegisterEndpoints: U3141LandRegisterEndpointsType = {
  INITLU: `${LOGICALUNIT_URL}/initLU`,
  SEARCH: `${LOGICALUNIT_URL}/search`,

  // Master Lot Chain Endpoints
  GET: (id: string | number) => `${LOGICALUNIT_URL}/${id}`,
  CREATE: `${LOGICALUNIT_URL}/create`,
  UPDATE: (id: string | number) => `${LOGICALUNIT_URL}/update/${id}`,
  DELETE: (id: string | number) => `${LOGICALUNIT_URL}/delete/${id}`,
  AUDITS: (id: string | number) => `${LOGICALUNIT_URL}/${id}/audits`,

  VIEWDOCUMENT: (documentId: string | number) => `${LOGICALUNIT_URL}/document/${documentId}`,
  VIEWDOCUMENTPDFFILE: (documentId: string | number) => `${LOGICALUNIT_URL}/document/${documentId}/pdf-file`,

  // Child Lot Chain Endpoints
  GETCHILDLOTCHAIN: (id: string | number, childLotChainId: string | number) => `${LOGICALUNIT_URL}/${id}/childlotchain/${childLotChainId}`,
  CREATECHILDLOTCHAIN: (id: string | number) => `${LOGICALUNIT_URL}/${id}/childlotchain/create`,
  LINKCONCORDANCEFORGRAPH: (id: string | number) => `${LOGICALUNIT_URL}/${id}/childlotchain/linkConcordanceForGraph`,
  UNLINKCONCORDANCEFORGRAPH: (id: string | number) => `${LOGICALUNIT_URL}/${id}/childlotchain/unlinkConcordanceForGraph`,
  RESOLVELOTDETAILFORGRAPHADD: (id: string | number) => `${LOGICALUNIT_URL}/${id}/childlotchain/resolveLotDetailForGraphAdd`,
  UPDATECHILDLOTCHAIN: (id: string | number, childLotChainId: string | number) => `${LOGICALUNIT_URL}/${id}/childlotchain/${childLotChainId}/update`,
  UPDATECHILDLOTCHAINFROMGRAPH: (id: string | number, childLotChainId: string | number) =>
    `${LOGICALUNIT_URL}/${id}/childlotchain/${childLotChainId}/updateFromGraph`,
  DELETECHILDLOTCHAIN: (id: string | number, childLotChainId: string | number) => `${LOGICALUNIT_URL}/${id}/childlotchain/${childLotChainId}/delete`,
  LOCKCHILDLOTCHAIN: (id: string | number, childLotChainId: string | number) => `${LOGICALUNIT_URL}/${id}/childlotchain/${childLotChainId}/lock`,
  UNLOCKCHILDLOTCHAIN: (id: string | number, childLotChainId: string | number) => `${LOGICALUNIT_URL}/${id}/childlotchain/${childLotChainId}/unlock`,
  FETCHPROPERTYINDEX: (id: string | number, childLotChainId: string | number) => `${LOGICALUNIT_URL}/${id}/childlotchain/${childLotChainId}/fetchPropertyIndex`,
  FETCHANNOTATEDLOTCHAININDEXPDF: (id: string | number, childLotChainId: string | number) =>
    `${LOGICALUNIT_URL}/${id}/childlotchain/${childLotChainId}/fetchAnnotatedLotChainIndexPdf`,
  FETCHANNOTATEDLOTCHAININDEXPDFDRYRUN: (id: string | number, childLotChainId: string | number) =>
    `${LOGICALUNIT_URL}/${id}/childlotchain/${childLotChainId}/fetchAnnotatedLotChainIndexPdf/dryRun`,
  CHILDLOTCHAINAUDITS: (id: string | number, childLotChainId: string | number) => `${LOGICALUNIT_URL}/${id}/childlotchain/${childLotChainId}/audits`,
  FINDPROPERTYDISTRICTS: `${LOGICALUNIT_URL}/findPropertyDistricts`,
  FINDCADASTRES: `${LOGICALUNIT_URL}/findCadastres`,
  FINDPROJECT: `${LOGICALUNIT_URL}/findProject`,
  FINDREQUESTEDLOT: `${LOGICALUNIT_URL}/findRequestedLot`,
  EXPORT_EXCEL: `${LOGICALUNIT_URL}/exportExcel`,
  FETCHCADASTRALHISTORY: (id: string | number) => `${LOGICALUNIT_URL}/${id}/fetchCadastralHistory`,
  RECONSTITUTECHAINFROMPDF: (id: string | number) => `${LOGICALUNIT_URL}/${id}/reconstituteChainFromPdf`,
  IMPORTCHAINANNOTATIONSDRYRUN: (id: string | number) =>
    `${LOGICALUNIT_URL}/${id}/importChainAnnotationsFromPdf/dryRun`,
  IMPORTCHAINANNOTATIONSAPPLY: (id: string | number) => `${LOGICALUNIT_URL}/${id}/importChainAnnotationsFromPdf`,
  CHAINIMPORTJOB: (jobId: string) => `${LOGICALUNIT_URL}/jobs/${jobId}`,
  CADASTRALHISTORYDOCUMENT: (id: string | number) => `${LOGICALUNIT_URL}/${id}/cadastralHistoryDocument`,
  INDEXDOCUMENT: (masterLotChainId: string | number, childLotChainId: string | number) =>
    `${LOGICALUNIT_URL}/${masterLotChainId}/childlotchain/${childLotChainId}/indexDocument`,
  PLANDOCUMENT: (masterLotChainId: string | number, childLotChainId: string | number) =>
    `${LOGICALUNIT_URL}/${masterLotChainId}/childlotchain/${childLotChainId}/planDocument`,
  SETVISIBLECHILDLOTCHAIN: (masterLotChainId: string | number, childLotChainId: string | number) =>
    `${LOGICALUNIT_URL}/${masterLotChainId}/childlotchain/${childLotChainId}/setVisible`,
  SETVISIBLE: (masterLotChainId: string | number) => `${LOGICALUNIT_URL}/${masterLotChainId}/setVisible`,
  NODEPOSITIONS: (masterLotChainId: string | number) => `${LOGICALUNIT_URL}/${masterLotChainId}/nodePositions`,
  LOTCHAINGRAPH: (masterLotChainId: string | number) => `${LOGICALUNIT_URL}/${masterLotChainId}/lotChainGraph`,
  GETDOCUMENTS: (masterLotChainId: string | number, childLotChainId: string | number) =>
    `${LOGICALUNIT_URL}/${masterLotChainId}/childlotchain/${childLotChainId}/documents`,
  POSTDOCUMENT: (masterLotChainId: string | number, childLotChainId: string | number) =>
    `${LOGICALUNIT_URL}/${masterLotChainId}/childlotchain/${childLotChainId}/documents`,
  DELETEDOCUMENT: (masterLotChainId: string | number, childLotChainId: string | number, documentId: string | number) =>
    `${LOGICALUNIT_URL}/${masterLotChainId}/childlotchain/${childLotChainId}/documents/${documentId}`,
  PATCHDOCUMENT: (masterLotChainId: string | number, childLotChainId: string | number, documentId: string | number) =>
    `${LOGICALUNIT_URL}/${masterLotChainId}/childlotchain/${childLotChainId}/documents/${documentId}`,
  PLAN_UPLOAD_TOKEN: (masterLotChainId: string | number) => `${LOGICALUNIT_URL}/${masterLotChainId}/plan-upload-token`,
  SITEAPPLI_VERSION: `${LOGICALUNIT_URL}/siteAppliSFoncier/version`,
};

/** Traduction d’un libellé de type d’opération lot detail (initLU). */
export interface U3141LotDetailOperationTypeTranslation {
  languageCode: string;
  operationTypeLabel: string;
}

/** Entrée de `lotDetailOperationTypes` renvoyée par initLU U3141 / U3142. */
export interface U3141LotDetailOperationTypeFromInitLU {
  id: string;
  operationTypeCode: string;
  translations: U3141LotDetailOperationTypeTranslation[];
  lotDetailSupplementaryInfoEnabled: boolean;
  active: boolean;
}

// Definition of the initialization parameters for the logical unit U3141
export interface U3141InitLU {
  launchSearch: boolean;
  hideSearch: boolean;
  nbRecordPerPage: number;
  /** Types de document acceptés en POST (dialogue « Ajouter un document », aligné backend). */
  addableDocumentTypes: string[];
  /** Types à emplacement unique : réimport = remplacement (BL-0708, ex. rfLotDetailIndex). */
  singleSlotDocumentTypes: string[];
  /** Types d’opération lot detail (remplace l’enum statique UI lorsque présent). */
  lotDetailOperationTypes: U3141LotDetailOperationTypeFromInitLU[];
  /** URL publique du manifeste version.json siteAppliSFoncier (BL-0456). Null = bouton masqué. */
  siteAppliSFoncierVersionUrl: string | null;
}

/** Critères autocomplete (POST /U3141/findProject). */
export type U3141AutoCompletCriterias = { fieldName: string; value: unknown }[];

/** Projet (findProject U3141). */
export interface U3141FindProjectItem {
  id: string;
  projetNumber?: string;
  projetName?: string;
  customerCode?: string;
  customerName?: string;
}

/** Lot demandé (findRequestedLot U3141). */
export interface U3141FindRequestedLotItem {
  requestedLot: string;
  /** Identifiant synthétique pour U2000AutoCompleteInput (égal à requestedLot). */
  id?: string;
}

/** Préremplissage création lot maître depuis U3121 (BL-0035). */
export interface U3141CreatePrefillState {
  projectNumber: string;
  projectDescription: string;
  requestedLot: string;
}

/** État React Router pour U3141 (BL-0035 prefill, BL-0710 graphe auto). */
export interface U3141LandRegisterLocationState {
  u3141CreatePrefill?: U3141CreatePrefillState;
  u3141ActionOriginFrom?: string;
  /** BL-0710 — ouvrir le dialogue graphe (Historique cadastral / chaîne de lots) après chargement fiche READ. */
  openLotChainGraph?: boolean;
}

export function u3141ParseMasterLotChainFromApiResult(data: unknown): U3141MasterLotChain | null {
  if (!data || typeof data !== "object") return null;
  const envelope = data as { result?: U3141MasterLotChain };
  const result = envelope.result ?? (data as U3141MasterLotChain);
  const id = result?.id;
  if (typeof id !== "string" || !id.trim()) return null;
  return result;
}

/** Sélection autocomplete lot demandé (UI). */
export interface U3141SelectedRequestedLotRef {
  id: string;
  requestedLot: string;
}

// Master Lot Chain type definition
export interface U3141MasterLotChain {
  id: string;
  projectNumber: string;
  projectDescription: string;
  requestedLot: string;
  createdAt: string;
  version: number;
  childLotChains: U3141ChildLotChain[];
  /** Présent lorsque le backend expose l’indicateur (GET lot). True si un document HTML d’historique cadastral est sauvegardé. @deprecated Utiliser documentTypes à la place. */
  hasHistoriqueCadastralSauvegarde?: boolean;
  /** Types de documents présents (backend). Utilisé pour activer le bouton Télécharger historique cadastral (ex. rfLotHistoriqueCadastral). */
  documentTypes?: U3141DocumentTypeEntry[];
  /** Projet sélectionné (autocomplete ajout) — UI uniquement, exclu du payload create/update. */
  selectedProject?: U3141FindProjectItem | null;
  /** Lot demandé sélectionné (autocomplete ajout) — UI uniquement. */
  selectedRequestedLot?: U3141SelectedRequestedLotRef | null;
}

// Child Lot Chain type definition

export interface U3141ChildMasterLotChainAudits {
  id: string;
  projectDescription: string;
  projectNumber: string;
}

/** Map des positions des nœuds du graphe (childLotChainId → { x, y }). */
export type U3141NodePositions = Record<string, { x: number; y: number }>;

export interface U3141ChildLotChain {
  id: string;
  /** Version optimistic locking (POST delete lot orphelin). */
  version?: number;
  masterLotChain: U3141ChildMasterLotChainAudits;
  lotDetail: U3141LotDetail;
  /** Id du child lot chain parent dans l'arbre du graphe (backend lotChainGraph). Utiliser pour construire l'arbre, pas le lot ni concordanceOrigines. */
  parentChildLotChainId?: string | null;
  /** Indique si un document « index » est disponible pour ce child lot chain (backend). */
  hasIndexDocument?: boolean;
  /** Indique si un document « plan » est disponible pour ce child lot chain (backend). */
  hasPlanDocument?: boolean;
  /** Indique si le child lot chain est visible dans le graphe (backend). */
  visible?: boolean;
  /** Position du nœud dans le graphe (backend). */
  nodePosition?: { x: number; y: number };
}

/** Payload POST suppression child lot chain orphelin (BL-0125). */
export interface U3141ChildLotChainDelete {
  id: string;
  version: number;
}

/** Body POST resolveLotDetailForGraphAdd (BL-0661, sans persistance). */
export interface U3141ResolveLotDetailForGraphAddBody {
  lot: string;
  /** Ligne autocomplete findSearchLots — évite le mauvais homonyme (ex. 23A). */
  lotDetailId?: string;
  codeDistrict?: string | null;
  cadastreCode?: string | null;
  rankCode?: string | null;
  rankLabel?: string | null;
}

export interface U3141ConcordancePreviewRow {
  entry: string;
  lot: string;
  propertyDistrict?: string | null;
  cadastre?: string | null;
  rank?: string | null;
  partieDeLot?: boolean;
}

/** Réponse resolveLotDetailForGraphAdd (lecture seule). */
export interface U3141ResolveLotDetailForGraphAddResult {
  found: boolean;
  alreadyInMasterChain: boolean;
  lotDetail?: {
    id: string;
    lot: string;
    partieDeLot?: boolean;
    propertyDistrict?: string | null;
    cadastre?: string | null;
    rank?: string | null;
    dateEstablishment?: string | null;
    operationType?: string | null;
    operationTypeInfo?: string | null;
    operationTypeInfoEnabled?: boolean;
    corrections?: string[] | null;
    lotState?: 'validate' | 'notValidate' | null;
    concordance: string[];
    concordancePartieDeLot?: boolean[];
  } | null;
  concordancePreview?: U3141ConcordancePreviewRow[];
}

/** Réponse POST create enrichi (BL-0661). */
export type U3141ChildLotChainCreateResult = U3141ChildLotChain & {
  lotChainGraph: U3141ChildLotChain[];
};

/** Types de document lotDetail (backend). API n'accepte que ces 4 valeurs à l'ajout. */
export const U3141_LOT_DETAIL_DOCUMENT_TYPES = [
  "rfLotDetailIndex",
  "rfLotDetailPlan",
  "rfLotPlansCorrectionCadastrale",
  "rfLotLivreRenvoi",
] as const;
export type U3141LotDetailDocumentType = (typeof U3141_LOT_DETAIL_DOCUMENT_TYPES)[number];

/** Tous les types affichés dans le combobox « Ajouter un document » (4 addables + Historique cadastral). */
export const U3141_DOCUMENT_TYPES_FOR_ADD_DIALOG = [
  ...U3141_LOT_DETAIL_DOCUMENT_TYPES,
  "rfLotHistoriqueCadastral",
] as const;

/** Types à emplacement unique (repli si initLU absent). */
export const U3141_SINGLE_SLOT_DOCUMENT_TYPES = ['rfLotDetailIndex'] as const;

export function u3141IsSingleSlotDocumentType(
  documentType: string,
  singleSlotDocumentTypes: readonly string[] = U3141_SINGLE_SLOT_DOCUMENT_TYPES,
): boolean {
  return singleSlotDocumentTypes.includes(documentType);
}

/** Plusieurs documents autorisés pour ce type dans le dialogue d’ajout (aligné backend BL-0708). */
export function u3141IsMultiAddableDocumentType(documentType: string): boolean {
  if (u3141IsSingleSlotDocumentType(documentType)) {
    return false;
  }
  return (U3141_DOCUMENT_TYPES_FOR_ADD_DIALOG as readonly string[]).includes(documentType);
}

/** @deprecated Préférer u3141IsMultiAddableDocumentType */
export const U3141_MULTI_DOCUMENT_TYPES = U3141_DOCUMENT_TYPES_FOR_ADD_DIALOG;

export function u3141IsMultiDocumentType(documentType: string): boolean {
  return u3141IsMultiAddableDocumentType(documentType);
}

/** Plafond fichiers par dialogue « Ajouter un document » (multi-types). */
export const U3141_DOCUMENT_UPLOAD_MAX_FILES = 50;

/** Un document du tableau lotDetail.documents. */
export interface U3141LotDetailDocument {
  id: string;
  documentName?: string;
  documentType?: string;
  project?: { id: string };
}

/** Entrée du tableau documentTypes (backend) : id, type et nom de fichier optionnel (ex. lotChainGraph). */
export interface U3141DocumentTypeEntry {
  id: string;
  documentType: string;
  documentName?: string | null;
  /** GET lotChainGraph : pour rfLotDetailAnnotatedLotChain, true si les annotations sont plus récentes que le document. */
  indexMostRecent?: boolean | null;
}

/** Détail d’une entrée de concordance (même ordre que `concordance`), optionnel dans la réponse GET. */
export interface U3141LotDetail {
  id: string;
  lot: string;
  /** Partie de lot (suffixe (ptie) dans les chaînes de concordance côté API). */
  partieDeLot?: boolean;
  district: U3141District;
  cadastre: U3141Cadastre;
  lotState: string;
  operationType: string;
  /** Texte libre complémentaire sur le type d’opération (API : operation_type_info). */
  operationTypeInfo?: string | null;
  /** Miroir GET (optionnel) : indique si le complément est prévu pour le type courant (UX). */
  operationTypeInfoEnabled?: boolean;
  concordance: string[];
  /** Même index que `concordance` — true si partie de lot (backend BL-0033). */
  concordancePartieDeLot?: boolean[];
  concordanceOrigines: string[];
  dateEstablishment: string;
  corrections: string;
  /** Tableau 0..n de documents (backend). */
  documents?: U3141LotDetailDocument[];
  /** Types de documents présents avec id (backend). Utilisé pour le menu du graphe et le bouton Consulter index. */
  documentTypes?: U3141DocumentTypeEntry[];
  /** GET lotChainGraph : true si l’index immeuble a des annotations PDF persistées (backend : indexAnnoter). */
  indexAnnoter?: boolean;
}

export interface U3141District {
  id: string;
  propertyDistrict: string;
  /** Code district (optionnel, pour format concordance complet). */
  codeDistrict?: string;
}

export interface U3141Cadastre {
  id: string;
  cadastre: string;
  rank: string;
  rankLabel: string;
  /** Code cadastre (optionnel, pour format concordance complet). */
  cadastreCode?: string;
  /** Code rang (ex. R03) (optionnel, pour format concordance complet). */
  rankCode?: string;
}

export interface U3141ViewDocument {
  signUrl: string;
}

/** Réponse du POST fetchHistoriqueCadastral (création du job) */
export interface U3141FetchHistoriqueCadastralJobResponse {
  id: string;
  status: string;
}

/** Réponse du POST reconstituteChainFromPdf (création du job) — BL-0562 */
export interface U3141ReconstituteChainFromPdfJobResponse {
  id: string;
  status: string;
}

/** Lot dry-run import annotations chaîne (BL-0700). */
export type U3141ImportChainAnnotationsDryRunLotStatus =
  | 'identical'
  | 'different'
  | 'missing_index'
  | 'no_shapes_in_pdf';

export interface U3141ImportChainAnnotationsDryRunLot {
  lotDetailId: string;
  lotLabel: string;
  status: U3141ImportChainAnnotationsDryRunLotStatus;
  existingShapeCount: number;
  extractedShapeCount: number;
}

export interface U3141ImportChainAnnotationsDryRunResponse {
  sourceDocumentId: string;
  masterLotChainId: string;
  lots: U3141ImportChainAnnotationsDryRunLot[];
}

export interface U3141ImportChainAnnotationsApplyBody {
  sourceDocumentId: string;
  lotDetailIdsToReplace: string[];
}

/** Réponse du POST importChainAnnotationsFromPdf (création du job) — BL-0700 */
export interface U3141ImportChainAnnotationsJobResponse {
  id: string;
  status: string;
}

/** Types d'alerte du job reconstitution chaîne PDF (miroir backend U3141ChainPdfAlerte). */
export type U3141ChainPdfAlerteType =
  | 'branches_absentes_du_pdf'
  | 'lien_deduit_du_numerotage_seulement'
  | 'aucun_lien_detecte';

/** Alerte métier renvoyée dans result.data.alertes du job RECONSTITUTE. */
export interface U3141ChainPdfAlerte {
  type: U3141ChainPdfAlerteType | string;
  lot: string | null;
  lots?: string[];
  explication: string;
}

/** Lot dont les formes U3140 ont été remplacées (BL-0653, annotationsImport.lotsRegeneres). */
export interface U3141ChainPdfLotRegenere {
  lot: string;
  replacedShapeCount: number;
  newShapeCount: number;
}

/** Job U2200 minimal (lecture result.data et progression pendant l'import). */
export interface U3141U2200Job {
  id: string;
  status: string;
  progressPercent?: number | null;
  progressData?: {
    percent?: number;
    step?: string;
    current?: number;
    total?: number;
    detail?: string;
  } | null;
  result?: {
    success?: boolean;
    data?: {
      ficheCount?: number;
      alerteCount?: number;
      alertes?: U3141ChainPdfAlerte[];
      masterLotChainId?: string;
      annotationsImport?: {
        lotsRegeneres?: U3141ChainPdfLotRegenere[];
        regenerateAnnotationsFromPdf?: boolean;
      };
      [key: string]: unknown;
    };
  } | null;
  errorMessage?: string | null;
}

/** Réponse du POST fetchPropertyIndex (création du job asynchrone) */
export interface U3141FetchPropertyIndexJobResponse {
  id: string;
  status: string;
}

/** Réponse du POST fetchAnnotatedLotChainIndexPdf (création du job asynchrone) */
export interface U3141FetchAnnotatedLotChainIndexPdfJobResponse {
  id: string;
  status: string;
}

/** Statut lot dry-run fusion chaîne annotée (BL-0704). */
export type U3141AnnotatedLotChainIndexMergeDryRunLotStatus =
  | 'ok'
  | 'missing_index'
  | 'will_be_masked'
  | 'anchor_not_visible';

export interface U3141AnnotatedLotChainIndexMergeDryRunLot {
  childLotChainId: string;
  lotDetailId: string;
  lotLabel: string;
  status: U3141AnnotatedLotChainIndexMergeDryRunLotStatus;
  indexDocumentId?: string;
  /** Annotations U3140 plus récentes que le PDF chaîne (`dirty` page index — parité graphe). */
  annotationsNewerThanChain?: boolean;
}

export interface U3141AnnotatedLotChainIndexMergeDryRunSummary {
  readyCount: number;
  missingIndexCount: number;
  maskedCount: number;
  mergeLotCount: number;
  canLaunchJob: boolean;
  blockReason?: string;
  annotationsNewerThanChainCount?: number;
}

/** Réponse POST dry-run fusion chaîne annotée (BL-0704, synchrone). */
export interface U3141AnnotatedLotChainIndexMergeDryRunResponse {
  masterLotChainId: string;
  anchorChildLotChainId: string;
  lots: U3141AnnotatedLotChainIndexMergeDryRunLot[];
  summary: U3141AnnotatedLotChainIndexMergeDryRunSummary;
}

/** Réponse du GET cadastralHistoryDocument (URL pré-signée S3). Le backend retourne un wrapper U2000ResultResponseDto (processingTimeMs, requestId, result, signUrl, type). */
export interface U3141CadastralHistoryDocumentResponse {
  processingTimeMs?: string;
  requestId?: string;
  result?: Record<string, unknown>;
  signUrl: string;
  type?: string;
}

/** Corps POST mint jeton plan-upload (aligné backend : pas de projectNumber côté UI). */
export interface U3141PlanUploadTokenMintRequest {
  clientRequestUuid: string;
}

/** Données métier dans result du POST plan-upload-token (201). */
export interface U3141PlanUploadTokenMintResult {
  accessToken: string;
  expiresAt: string;
}

// Empty Objects
export const U3141EmptyLandRegister: U3141MasterLotChain = {
  id: "",
  projectNumber: "",
  projectDescription: "",
  requestedLot: "",
  createdAt: "",
  version: 0,
  childLotChains: [],
  selectedProject: null,
  selectedRequestedLot: null,
};

export const U3141EmptyChildLotChain: U3141ChildLotChain = {
  id: "",
  masterLotChain: {
    id: "",
    projectDescription: "",
    projectNumber: "",
  },
  lotDetail: {
    id: "",
    lot: "",
    partieDeLot: false,
    district: {
      id: "",
      propertyDistrict: "",
      codeDistrict: "",
    },
    cadastre: {
      id: "",
      cadastre: "",
      rank: "",
      rankLabel: "",
      cadastreCode: "",
      rankCode: "",
    },
    dateEstablishment: "",
    lotState: "",
    operationType: "",
    operationTypeInfo: null,
    concordance: [],
    concordancePartieDeLot: [],
    concordanceOrigines: [],
    corrections: "",
    documents: [],
  },
};

/** Payload API lot maître sans champs UI (selectedProject, selectedRequestedLot). */
export function u3141MasterLotChainToApiPayload(data: U3141MasterLotChain): U3141MasterLotChain {
  const { selectedProject: _selectedProject, selectedRequestedLot: _selectedRequestedLot, ...rest } = data;
  return rest as U3141MasterLotChain;
}

// API Calls - Mint plan upload token (ZIP externe)
export const doMintPlanUploadToken = (
  axios: AxiosInstance,
  masterLotChainId: string | number,
  body: U3141PlanUploadTokenMintRequest
) => {
  return axios.post<U3141PlanUploadTokenMintResult | { result: U3141PlanUploadTokenMintResult }>(
    u3141LandRegisterEndpoints.PLAN_UPLOAD_TOKEN(masterLotChainId),
    body
  );
};

/** Proxy backend du manifeste version.json (évite CORS CloudFront, BL-0456). */
export const doGetSiteAppliSFoncierVersion = (axios: AxiosInstance) => {
  return axios.get<{ result: unknown } | unknown>(u3141LandRegisterEndpoints.SITEAPPLI_VERSION);
};

// API Calls - Initialization
export const doInitLU = (axios: AxiosInstance) => {
  return axios.get(u3141LandRegisterEndpoints.INITLU);
};

// API Calls - Search Land Register
export const doSearch = (axios: AxiosInstance, searchPayload: U2000SearchPayload) => {
  return axios.post(u3141LandRegisterEndpoints.SEARCH, searchPayload);
};

// API Calls - Get Master Lot Chain by ID
export const doGetData = (axios: AxiosInstance, id: string | number, forceRefresh?: boolean) => {
  const params = {
    ...(forceRefresh && { b: U2000GenerateUrlUUID() }),
  };
  return axios.get(u3141LandRegisterEndpoints.GET(id), { params });
};

// API Calls - Create Master Lot Chain
export const doCreateData = (axios: AxiosInstance, data: U3141MasterLotChain) => {
  return axios.post(u3141LandRegisterEndpoints.CREATE, data);
};

// API Calls - Update Master Lot Chain
export const doUpdateData = (axios: AxiosInstance, id: string | number, data: U3141MasterLotChain) => {
  return axios.post(u3141LandRegisterEndpoints.UPDATE(id), data);
};

// API Calls - Delete Master Lot Chain
export const doDeleteData = (axios: AxiosInstance, id: string | number, data?: U3141MasterLotChain) => {
  return axios.post(u3141LandRegisterEndpoints.DELETE(id), data);
};

// API Call - Audits Master Lot Chain
export const doGetAuditsData = (axios: AxiosInstance, id: string | number) => {
  const params = {};
  return axios.get(u3141LandRegisterEndpoints.AUDITS(id), { params });
};

// API Calls - Get Child Lot Chain by ID
export const doGetChildLotChainData = (axios: AxiosInstance, masterLotChainId: string | number, childLotChainId: string | number, forceRefresh?: boolean) => {
  const params = forceRefresh ? { b: U2000GenerateUrlUUID() } : {};
  return axios.get(u3141LandRegisterEndpoints.GETCHILDLOTCHAIN(masterLotChainId, childLotChainId), { params });
};

// API Calls - Create Child Lot Chain (body plat BL-0661 — voir u3141MapChildLotChainToCreatePayload)
export type { U3141ChildLotChainCreatePayload } from './tools/u3141ChildLotChainCreateMapper';

export const doCreateChildLotChainData = (
  axios: AxiosInstance,
  masterLotChainId: string | number,
  data: import('./tools/u3141ChildLotChainCreateMapper').U3141ChildLotChainCreatePayload,
) => {
  return axios.post(u3141LandRegisterEndpoints.CREATECHILDLOTCHAIN(masterLotChainId), data);
};

/** Parse la réponse POST create (child principal + lotChainGraph). */
export type U3141GraphConcordanceMutationResult = {
  lotChainGraph: U3141ChildLotChain[];
};

export function u3141ParseGraphConcordanceMutationResponse(data: unknown): U3141ChildLotChain[] | null {
  if (!data || typeof data !== "object") return null;
  const envelope = data as { result?: U3141GraphConcordanceMutationResult };
  const result = envelope.result ?? (data as U3141GraphConcordanceMutationResult);
  const graph = result?.lotChainGraph;
  return Array.isArray(graph) ? graph : null;
}

export const doLinkConcordanceForGraph = (
  axios: AxiosInstance,
  masterLotChainId: string | number,
  body: {
    concordanceHolderChildLotChainId: string;
    concordanceOriginChildLotChainId: string;
    sourceChildLotChainId: string;
    targetChildLotChainId: string;
    sourceHandle: "top" | "bottom";
    targetHandle: "top" | "bottom";
  },
) => {
  return axios.post(u3141LandRegisterEndpoints.LINKCONCORDANCEFORGRAPH(masterLotChainId), body);
};

export const doUnlinkConcordanceForGraph = (
  axios: AxiosInstance,
  masterLotChainId: string | number,
  body: { concordanceHolderChildLotChainId: string; concordanceOriginChildLotChainId: string },
) => {
  return axios.post(u3141LandRegisterEndpoints.UNLINKCONCORDANCEFORGRAPH(masterLotChainId), body);
};

export function u3141ParseChildLotChainCreateResponse(data: unknown): U3141ChildLotChainCreateResult | null {
  if (!data || typeof data !== 'object') return null;
  const envelope = data as { result?: U3141ChildLotChainCreateResult };
  const result = envelope.result ?? (data as U3141ChildLotChainCreateResult);
  if (!result || typeof result !== 'object' || !result.id) return null;
  const graph = Array.isArray(result.lotChainGraph) ? result.lotChainGraph : [];
  return { ...result, lotChainGraph: graph };
}

export const doResolveLotDetailForGraphAdd = (
  axios: AxiosInstance,
  masterLotChainId: string | number,
  body: U3141ResolveLotDetailForGraphAddBody,
) => {
  return axios.post<{ result?: U3141ResolveLotDetailForGraphAddResult }>(
    u3141LandRegisterEndpoints.RESOLVELOTDETAILFORGRAPHADD(masterLotChainId),
    body,
  );
};

// API Calls - Update Child Lot Chain
export const doUpdateChildLotChainData = (axios: AxiosInstance, masterLotChainId: string | number, childLotChainId: string | number, data: U3141ChildLotChain) => {
  return axios.post(u3141LandRegisterEndpoints.UPDATECHILDLOTCHAIN(masterLotChainId, childLotChainId), data);
};

/** BL-0697 — update depuis dialogue graphe (champs métier facultatifs). */
export const doUpdateChildLotChainFromGraphData = (
  axios: AxiosInstance,
  masterLotChainId: string | number,
  childLotChainId: string | number,
  data: U3141ChildLotChain,
) => {
  return axios.post(u3141LandRegisterEndpoints.UPDATECHILDLOTCHAINFROMGRAPH(masterLotChainId, childLotChainId), data);
};

// API Calls - Delete Child Lot Chain (lot orphelin, BL-0125)
export const doDeleteChildLotChainData = (
  axios: AxiosInstance,
  masterLotChainId: string | number,
  childLotChainId: string | number,
  data: U3141ChildLotChainDelete,
) => {
  return axios.post(u3141LandRegisterEndpoints.DELETECHILDLOTCHAIN(masterLotChainId, childLotChainId), data);
};

// API Calls - Lock Child Lot Chain
export const doLockChildLotChainData = (axios: AxiosInstance, masterLotChainId: string | number, childLotChainId: string | number) => {
  return axios.get(u3141LandRegisterEndpoints.LOCKCHILDLOTCHAIN(masterLotChainId, childLotChainId));
};

// API Calls - Unlock Child Lot Chain
export const doUnlockChildLotChainData = (axios: AxiosInstance, masterLotChainId: string | number, childLotChainId: string | number) => {
  return axios.get(u3141LandRegisterEndpoints.UNLOCKCHILDLOTCHAIN(masterLotChainId, childLotChainId));
};

// API Calls - Fetch Property Index (crée un job asynchrone, retourne { id, status })
export const doFetchPropertyIndex = (axios: AxiosInstance, masterLotChainId: string | number, childLotChainId: string | number) => {
  return axios.post<U3141FetchPropertyIndexJobResponse>(u3141LandRegisterEndpoints.FETCHPROPERTYINDEX(masterLotChainId, childLotChainId));
};

// API Calls - Fusion index PDF chaîne annotée (crée un job asynchrone, retourne { id, status })
/** POST dry-run fusion chaîne annotée (BL-0704, synchrone). */
export const doFetchAnnotatedLotChainIndexPdfDryRun = (
  axios: AxiosInstance,
  masterLotChainId: string | number,
  childLotChainId: string | number,
) => {
  return axios.post<U3141AnnotatedLotChainIndexMergeDryRunResponse>(
    u3141LandRegisterEndpoints.FETCHANNOTATEDLOTCHAININDEXPDFDRYRUN(masterLotChainId, childLotChainId),
  );
};

export const doFetchAnnotatedLotChainIndexPdf = (axios: AxiosInstance, masterLotChainId: string | number, childLotChainId: string | number) => {
  return axios.post<U3141FetchAnnotatedLotChainIndexPdfJobResponse>(
    u3141LandRegisterEndpoints.FETCHANNOTATEDLOTCHAININDEXPDF(masterLotChainId, childLotChainId)
  );
};

// API Calls - Search Property Districts
export const doSearchPropertyDistricts = (axios: AxiosInstance, criterias: any) => {
  return axios.post(u3141LandRegisterEndpoints.FINDPROPERTYDISTRICTS, criterias);
};

// API Calls - Search Cadastres
export const doSearchCadastres = (axios: AxiosInstance, criterias: any) => {
  return axios.post(u3141LandRegisterEndpoints.FINDCADASTRES, criterias);
};

// API Calls - Find Project (autocomplete lot maître)
export const doFindProject = (axios: AxiosInstance, criterias: U3141AutoCompletCriterias) => {
  return axios.post(u3141LandRegisterEndpoints.FINDPROJECT, criterias);
};

// API Calls - Find Requested Lot (autocomplete lot maître — projet à arpenter)
export const doFindRequestedLot = (axios: AxiosInstance, criterias: U3141AutoCompletCriterias) => {
  return axios.post(u3141LandRegisterEndpoints.FINDREQUESTEDLOT, criterias);
};

// API Calls - View Document
export const doViewDocument = (axios: AxiosInstance, documentId: string | number) => {
  return axios.get(u3141LandRegisterEndpoints.VIEWDOCUMENT(documentId));
};

/**
 * GET …/document/:id/pdf-file — buffer binaire (NAS si ACK, sinon cache S3).
 * BL-0472 : lecture vérité pour index / plan / historique.
 */
export const doGetDocumentPdfFile = async (
  axios: AxiosInstance,
  documentId: string | number
): Promise<{ buffer: ArrayBuffer; contentType: string | null }> => {
  const res = await axios.get<ArrayBuffer>(u3141LandRegisterEndpoints.VIEWDOCUMENTPDFFILE(documentId), {
    responseType: "arraybuffer",
  });
  const rawHeader = res.headers?.["content-type"] ?? res.headers?.["Content-Type"];
  const contentType = typeof rawHeader === "string" ? rawHeader : null;
  return { buffer: res.data, contentType };
};

// API Call - Audits Child Lot Chain
export const doGetChildLotChainAuditsData = (axios: AxiosInstance, masterLotChainId: string | number, childLotChainId: string | number) => {
  const params = {};
  return axios.get(u3141LandRegisterEndpoints.CHILDLOTCHAINAUDITS(masterLotChainId, childLotChainId), { params });
};

// API Calls - Export Excel
export const doExportExcel = (axios: AxiosInstance, filter: U2000SearchParameter, lang?: "fr" | "en") => {
  return axios.post(u3141LandRegisterEndpoints.EXPORT_EXCEL, { filter, lang });
};

// API Calls - Fetch Historique Cadastral (crée un job asynchrone, retourne { id, status })
export const doFetchHistoriqueCadastral = (axios: AxiosInstance, masterLotChainId: string | number) => {
  return axios.post<U3141FetchHistoriqueCadastralJobResponse>(u3141LandRegisterEndpoints.FETCHCADASTRALHISTORY(masterLotChainId));
};

/**
 * POST import PDF de chaîne → job U3141_RECONSTITUTE_CHAIN_FROM_PDF.
 * BL-0653 (décision produit provisoire) : `regenerateAnnotationsFromPdf` est toujours `"true"`.
 */
export const doReconstituteChainFromPdf = (
  axios: AxiosInstance,
  masterLotChainId: string | number,
  file: File
) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('regenerateAnnotationsFromPdf', 'true');
  return axios.post<U3141ReconstituteChainFromPdfJobResponse>(
    u3141LandRegisterEndpoints.RECONSTITUTECHAINFROMPDF(masterLotChainId),
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
};

/** POST dry-run import annotations chaîne (BL-0700, synchrone). */
export const doImportChainAnnotationsDryRun = (
  axios: AxiosInstance,
  masterLotChainId: string | number,
  file: File,
) => {
  const formData = new FormData();
  formData.append('file', file);
  return axios.post<U3141ImportChainAnnotationsDryRunResponse>(
    u3141LandRegisterEndpoints.IMPORTCHAINANNOTATIONSDRYRUN(masterLotChainId),
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
};

/** POST apply import annotations chaîne → job U3141_IMPORT_CHAIN_ANNOTATIONS_FROM_PDF (BL-0700). */
export const doImportChainAnnotationsApply = (
  axios: AxiosInstance,
  masterLotChainId: string | number,
  body: U3141ImportChainAnnotationsApplyBody,
) => {
  return axios.post<U3141ImportChainAnnotationsJobResponse>(
    u3141LandRegisterEndpoints.IMPORTCHAINANNOTATIONSAPPLY(masterLotChainId),
    body,
  );
};

const U2200_JOB_GET = (jobId: string) => `U2200/jobs/${jobId}`;

/** GET job U2200 (lecture alertes après reconstitution — droit U2200:READ). */
export const doGetU2200Job = (axios: AxiosInstance, jobId: string) => {
  return axios.get<{ result?: U3141U2200Job } & U3141U2200Job>(U2200_JOB_GET(jobId));
};

/** GET progression de l'import de chaîne (créateur du job, rôle REGFON). */
export const doGetChainImportJob = (axios: AxiosInstance, jobId: string) => {
  return axios.get<{ result?: U3141U2200Job } & U3141U2200Job>(u3141LandRegisterEndpoints.CHAINIMPORTJOB(jobId));
};

export type U3141ChainImportProgress = {
  percent: number;
  step: string | null;
  current?: number;
  total?: number;
  detail?: string;
};

/** Lit l'étape courante depuis un job d'import de chaîne. */
export function u3141ReadChainImportProgress(job: U3141U2200Job | null): U3141ChainImportProgress {
  const data = job?.progressData;
  const percentFromData = typeof data?.percent === 'number' ? data.percent : null;
  const percent = percentFromData ?? (typeof job?.progressPercent === 'number' ? job.progressPercent : 0);
  return {
    percent: Math.max(0, Math.min(100, Math.round(percent))),
    step: typeof data?.step === 'string' ? data.step : null,
    current: typeof data?.current === 'number' ? data.current : undefined,
    total: typeof data?.total === 'number' ? data.total : undefined,
    detail: typeof data?.detail === 'string' && data.detail.trim() ? data.detail : undefined,
  };
}

/** Extrait le job depuis une réponse U2200 (enveloppe `{ result }` ou job plat). */
export function u3141ParseU2200JobResponse(data: unknown): U3141U2200Job | null {
  if (!data || typeof data !== 'object') return null;
  const envelope = data as { result?: unknown; id?: unknown; status?: unknown };
  const raw = envelope.result && typeof envelope.result === 'object' ? envelope.result : data;
  if (!raw || typeof raw !== 'object') return null;
  const job = raw as U3141U2200Job;
  if (typeof job.id !== 'string' || typeof job.status !== 'string') return null;
  return job;
}

/** Extrait annotationsImport.lotsRegeneres (BL-0653). */
export function u3141ExtractChainPdfLotsRegeneresFromJob(job: U3141U2200Job | null): U3141ChainPdfLotRegenere[] {
  const rows = job?.result?.data?.annotationsImport?.lotsRegeneres;
  if (!Array.isArray(rows)) return [];
  return rows.filter(
    (row): row is U3141ChainPdfLotRegenere =>
      !!row &&
      typeof row === 'object' &&
      typeof row.lot === 'string' &&
      typeof row.replacedShapeCount === 'number' &&
      typeof row.newShapeCount === 'number'
  );
}

/** Extrait alertes[] depuis un job U2200 terminé (reconstitution). */
export function u3141ExtractChainPdfAlertesFromJob(job: U3141U2200Job | null): U3141ChainPdfAlerte[] {
  const alertes = job?.result?.data?.alertes;
  if (!Array.isArray(alertes)) return [];
  return alertes.filter(
    (a): a is U3141ChainPdfAlerte =>
      !!a && typeof a === 'object' && typeof (a as U3141ChainPdfAlerte).explication === 'string'
  );
}

export const doGetCadastralHistoryDocumentUrl = (axios: AxiosInstance, masterLotChainId: string | number) => {
  return axios.get<U3141CadastralHistoryDocumentResponse>(u3141LandRegisterEndpoints.CADASTRALHISTORYDOCUMENT(masterLotChainId));
};

// API Calls - Index document (master + child) → signUrl pour ouverture nouvel onglet
export const doGetIndexDocumentUrl = (axios: AxiosInstance, masterLotChainId: string | number, childLotChainId: string | number) => {
  return axios.get<U3141CadastralHistoryDocumentResponse>(u3141LandRegisterEndpoints.INDEXDOCUMENT(masterLotChainId, childLotChainId));
};

// API Calls - Plan document (master + child) → signUrl pour ouverture nouvel onglet
export const doGetPlanDocumentUrl = (axios: AxiosInstance, masterLotChainId: string | number, childLotChainId: string | number) => {
  return axios.get<U3141CadastralHistoryDocumentResponse>(u3141LandRegisterEndpoints.PLANDOCUMENT(masterLotChainId, childLotChainId));
};

// API Calls - Visible child lot chain (persistance côté serveur)

/** Mise à jour visible en bulk (recommandé). PATCH /U3141/{masterLotChainId}/setVisible */
export const doSetVisibleBulk = (
  axios: AxiosInstance,
  masterLotChainId: string | number,
  childLotChainIds: string[],
  visible: boolean
) => {
  return axios.patch(u3141LandRegisterEndpoints.SETVISIBLE(masterLotChainId), { childLotChainIds, visible });
};

/** Mise à jour visible pour un seul nœud (optionnel). */
export const doSetVisibleChildLotChainData = (
  axios: AxiosInstance,
  masterLotChainId: string | number,
  childLotChainId: string | number,
  visible: boolean
) => {
  return axios.patch(u3141LandRegisterEndpoints.SETVISIBLECHILDLOTCHAIN(masterLotChainId, childLotChainId), { visible });
};

/** Sauvegarde les positions des nœuds du graphe (childLotChainId → { x, y }). */
export const doSaveNodePositions = (
  axios: AxiosInstance,
  masterLotChainId: string | number,
  positions: U3141NodePositions
) => {
  return axios.patch(u3141LandRegisterEndpoints.NODEPOSITIONS(masterLotChainId), { positions });
};

/** Réinitialise les positions des nœuds (efface les positions sauvegardées). */
export const doResetNodePositions = (axios: AxiosInstance, masterLotChainId: string | number) => {
  return axios.patch(u3141LandRegisterEndpoints.NODEPOSITIONS(masterLotChainId), { positions: {} });
};

/**
 * Récupère les données de la chaîne de lots pour le graphe (endpoint dédié).
 * Réponse attendue : tableau de child lot chains ou wrapper { result } / { results } / { data } (ex. U2000ResultsResponseDto).
 */
export const doGetLotChainGraphData = (
  axios: AxiosInstance,
  masterLotChainId: string | number
): Promise<U3141ChildLotChain[]> => {
  return axios
    .get(u3141LandRegisterEndpoints.LOTCHAINGRAPH(masterLotChainId))
    .then((res) => {
      const raw = res.data;
      if (Array.isArray(raw)) return raw;
      if (raw?.results && Array.isArray(raw.results)) return raw.results;
      if (raw?.result && Array.isArray(raw.result)) return raw.result;
      if (raw?.data && Array.isArray(raw.data)) return raw.data;
      return [];
    });
};

/**
 * Retourne l'id du premier document de type index (rfLotDetailIndex).
 * Utilise documentTypes ou documents (voir getDocumentIdByType).
 */
export function getIndexDocumentId(lotDetail: U3141LotDetail | null | undefined): string | null {
  return getDocumentIdByType(lotDetail, "rfLotDetailIndex");
}

/**
 * Retourne l'id du premier document du type donné.
 * Utilise documentTypes (nouvelle structure { id, documentType }) si présent, sinon lotDetail.documents.
 */
export function getDocumentIdByType(
  lotDetail: U3141LotDetail | null | undefined,
  documentType: string
): string | null {
  if (!documentType) return null;
  const dt = lotDetail?.documentTypes;
  if (Array.isArray(dt)) {
    const entry = dt.find((e) => e && typeof e === "object" && "documentType" in e && e.documentType === documentType);
    if (entry && "id" in entry) return entry.id;
  }
  const docs = lotDetail?.documents;
  if (Array.isArray(docs)) {
    const found = docs.find((d) => d.documentType === documentType);
    return found?.id ?? null;
  }
  return null;
}

// API Calls - Documents du child lot chain (onglet Documents)

/** GET liste des documents. Réponse : tableau ou wrapper result/results/data. */
export const doGetDocumentsList = (
  axios: AxiosInstance,
  masterLotChainId: string | number,
  childLotChainId: string | number
): Promise<U3141LotDetailDocument[]> => {
  return axios
    .get(u3141LandRegisterEndpoints.GETDOCUMENTS(masterLotChainId, childLotChainId))
    .then((res) => {
      const raw = res.data;
      if (Array.isArray(raw)) return raw;
      if (raw?.results && Array.isArray(raw.results)) return raw.results;
      if (raw?.result && Array.isArray(raw.result)) return raw.result;
      if (raw?.data && Array.isArray(raw.data)) return raw.data;
      return [];
    });
};

/** POST création document (multipart: file + documentType). */
export const doCreateDocument = (
  axios: AxiosInstance,
  masterLotChainId: string | number,
  childLotChainId: string | number,
  formData: FormData
) => {
  return axios.post(u3141LandRegisterEndpoints.POSTDOCUMENT(masterLotChainId, childLotChainId), formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

/** DELETE document. */
export const doDeleteDocument = (
  axios: AxiosInstance,
  masterLotChainId: string | number,
  childLotChainId: string | number,
  documentId: string | number
) => {
  return axios.delete(u3141LandRegisterEndpoints.DELETEDOCUMENT(masterLotChainId, childLotChainId, documentId));
};

/** PATCH document (corps JSON : documentName). */
export const doPatchDocumentName = (
  axios: AxiosInstance,
  masterLotChainId: string | number,
  childLotChainId: string | number,
  documentId: string | number,
  documentName: string
) => {
  return axios.patch(u3141LandRegisterEndpoints.PATCHDOCUMENT(masterLotChainId, childLotChainId, documentId), {
    documentName,
  });
};

export const U3141_ERROR_MAIN_LOT_NOT_DELETABLE = "BU314121";
export const U3141_ERROR_CHILD_LOT_ALREADY_IN_CHAIN = "BU314125";
export const U3141_ERROR_INVALID_GRAPH_CONCORDANCE_HANDLES = "BU314126";
export const U3141_ERROR_GRAPH_CONCORDANCE_ALREADY_LINKED = "BU314127";
export const U3141_ERROR_GRAPH_CONCORDANCE_NOT_LINKED = "BU314128";
export const U3141_ERROR_CHAIN_PDF_SCRAPING_FED = "BU314122";
export const U3141_ERROR_CHAIN_PDF_NO_INDEX_PAGE = "BU314123";
export const U3141_ERROR_CHAIN_PDF_SOURCE_DOC_NOT_FOUND = "BU314124";
export const U3141_ERROR_CHAIN_PDF_IMPORT_ANNOTATIONS_INVALID_LOT = "BU314129";

// Handle API Errors for U3141 Logical Unit
export function luHandleAppiError(t: TranslateFn, NS: string, err: { code?: string }, errorResult: U2000ApiErrorResult) {
  if (err?.code === U3141_ERROR_MAIN_LOT_NOT_DELETABLE) {
    errorResult.genericErrors.push({
      title: t("errorTitle", { ns: NS }),
      text: t("errMainLotNotDeletable", { ns: NS }),
    });
  }
  if (err?.code === U3141_ERROR_CHILD_LOT_ALREADY_IN_CHAIN) {
    errorResult.genericErrors.push({
      title: t("errorTitle", { ns: NS }),
      text: t("errChildLotAlreadyInChain", { ns: NS }),
    });
  }
  if (err?.code === U3141_ERROR_INVALID_GRAPH_CONCORDANCE_HANDLES) {
    errorResult.genericErrors.push({
      title: t("errorTitle", { ns: NS }),
      text: t("errGraphConcordanceInvalidHandles", { ns: NS }),
    });
  }
  if (err?.code === U3141_ERROR_GRAPH_CONCORDANCE_ALREADY_LINKED) {
    errorResult.genericErrors.push({
      title: t("errorTitle", { ns: NS }),
      text: t("errGraphConcordanceAlreadyLinked", { ns: NS }),
    });
  }
  if (err?.code === U3141_ERROR_GRAPH_CONCORDANCE_NOT_LINKED) {
    errorResult.genericErrors.push({
      title: t("errorTitle", { ns: NS }),
      text: t("errGraphConcordanceNotLinked", { ns: NS }),
    });
  }
  if (err?.code === U3141_ERROR_CHAIN_PDF_SCRAPING_FED) {
    errorResult.genericErrors.push({
      title: t("errorTitle", { ns: NS }),
      text: t("errBU314122", { ns: NS }),
    });
  }
  if (err?.code === U3141_ERROR_CHAIN_PDF_NO_INDEX_PAGE) {
    errorResult.genericErrors.push({
      title: t("errorTitle", { ns: NS }),
      text: t("errBU314123", { ns: NS }),
    });
  }
  if (err?.code === U3141_ERROR_CHAIN_PDF_SOURCE_DOC_NOT_FOUND) {
    errorResult.genericErrors.push({
      title: t("errorTitle", { ns: NS }),
      text: t("errBU314124", { ns: NS }),
    });
  }
  if (err?.code === U3141_ERROR_CHAIN_PDF_IMPORT_ANNOTATIONS_INVALID_LOT) {
    errorResult.genericErrors.push({
      title: t("errorTitle", { ns: NS }),
      text: t("errBU314129", { ns: NS }),
    });
  }
}
